import { FunctionComponent, SVGProps } from "react";
import { ReactComponent as PensiveFaceEmoji } from "shared/assets/Icons/emoji-pensive-face.svg";
import { ReactComponent as AnxiousFaceEmoji } from "shared/assets/Icons/emoji-anxious-face.svg";
import { ReactComponent as SleepingFaceEmoji } from "shared/assets/Icons/emoji-sleeping-face.svg";
import { ReactComponent as DisappointedFaceEmoji } from "shared/assets/Icons/emoji-disappointed-face.svg";
import { ReactComponent as ThinkingFaceEmoji } from "shared/assets/Icons/emoji-thinking-face.svg";
import { ReactComponent as SpeechBalloonEmoji } from "shared/assets/Icons/emoji-speech-balloon.svg";
import { ReactComponent as FaceHappyEmoji } from "shared/assets/Icons/emoji-smiling-face.svg";
import { ReactComponent as FaceNeutralEmoji } from "shared/assets/Icons/emoji-neutral-face.svg";
import { ReactComponent as FaceSadEmoji } from "shared/assets/Icons/emoji-pensive-face.svg";
import {
  FeelingResources,
  LearningMaterialSummary,
  SupportMomentFeelingType,
  SupportMomentResourceEffectType,
} from "shared/request/myHealthyAdvantageApi";
import { WellKnownLearningMaterialSubCategories } from "UIPalette/LearningMaterials/wellKnownLearningMaterials";
import { MenuItem } from "UIPalette/CardMenuList/CardMenuList";

export enum SupportOption {
  Overwhelmed = "overwhelmed",
  Anxious = "anxious",
  Exhausted = "exhausted",
  FeelingLow = "feelingLow",
  NotSure = "notSure",
  JustChat = "justChat",
}

export enum CheckInOutcome {
  Better = "better",
  Same = "same",
  Worse = "worse",
}

export type SupportOptionConfig = {
  Emoji: FunctionComponent<SVGProps<SVGSVGElement>>;
  destination: string;
};

export type NotSureRoutingOption = "calmMyMind" | "restMyBody" | "noticeSomethingPositive";

export type ResourceFilter = {
  id: string;
  title: string;
};

export const SELECTION_DELAY_MS = 500;
export const COACHMARK_DELAY_MS = 500;

export const SUPPORT_OPTION_CONFIG: Record<SupportOption, SupportOptionConfig> = {
  [SupportOption.Overwhelmed]: { Emoji: PensiveFaceEmoji, destination: "/support-moments" },
  [SupportOption.Anxious]: { Emoji: AnxiousFaceEmoji, destination: "/support-moments" },
  [SupportOption.Exhausted]: { Emoji: SleepingFaceEmoji, destination: "/support-moments" },
  [SupportOption.FeelingLow]: { Emoji: DisappointedFaceEmoji, destination: "/support-moments" },
  [SupportOption.NotSure]: { Emoji: ThinkingFaceEmoji, destination: "/support-moments" },
  [SupportOption.JustChat]: { Emoji: SpeechBalloonEmoji, destination: "/support" },
};

export const SUPPORT_OPTION_FEELING_MAP: Partial<Record<SupportOption, SupportMomentFeelingType>> = {
  [SupportOption.Overwhelmed]: 1,
  [SupportOption.Anxious]: 2,
  [SupportOption.Exhausted]: 3,
  [SupportOption.FeelingLow]: 4,
};

export const NOT_SURE_ROUTING_FEELING_MAP: Record<NotSureRoutingOption, SupportMomentFeelingType[]> = {
  calmMyMind: [1, 2],
  restMyBody: [3],
  noticeSomethingPositive: [4],
};

export const CHECK_IN_OPTION_CONFIG: Record<
  CheckInOutcome,
  { Emoji: MenuItem["emoji"]; theme: MenuItem["theme"]; effect: SupportMomentResourceEffectType }
> = {
  [CheckInOutcome.Better]: { Emoji: FaceHappyEmoji, theme: "green", effect: 1 },
  [CheckInOutcome.Same]: { Emoji: FaceNeutralEmoji, theme: "orange", effect: 2 },
  [CheckInOutcome.Worse]: { Emoji: FaceSadEmoji, theme: "red", effect: 3 },
};

export const FEELING_RESOURCE_FILTER_MAP: Partial<Record<SupportMomentFeelingType, ResourceFilter>> = {
  [SUPPORT_OPTION_FEELING_MAP[SupportOption.Anxious] as SupportMomentFeelingType]: {
    id: WellKnownLearningMaterialSubCategories.anxious,
    title: "anxiety",
  },
};

export const getResourceFilterForFeeling = (feeling: SupportMomentFeelingType): ResourceFilter | undefined => FEELING_RESOURCE_FILTER_MAP[feeling];

export const getResourcesForFeelings = (
  resourcesByFeeling: FeelingResources[] | undefined,
  feelings: SupportMomentFeelingType[]
): LearningMaterialSummary[] | undefined => {
  const matched = resourcesByFeeling?.filter((f) => feelings.includes(f.feeling)).flatMap((f) => f.resources);
  return matched?.length ? matched : undefined;
};

export type SupportMomentsStage =
  | "generalError"
  | "notSureRouting"
  | "recommendedResource"
  | "resourceNotFound"
  | "checkIn"
  | "positiveOutcome"
  | "neutralOutcome"
  | "negativeOutcome";

const OUTCOME_STAGE: Record<CheckInOutcome, SupportMomentsStage> = {
  [CheckInOutcome.Better]: "positiveOutcome",
  [CheckInOutcome.Same]: "neutralOutcome",
  [CheckInOutcome.Worse]: "negativeOutcome",
};

export const POSITIVE_MOMENT_STAGES: SupportMomentsStage[] = ["checkIn", "positiveOutcome", "neutralOutcome"];

const JOURNEY_STEP_STAGES: SupportMomentsStage[] = ["recommendedResource", "checkIn"];
const NOT_SURE_JOURNEY_STEP_STAGES: SupportMomentsStage[] = ["notSureRouting", ...JOURNEY_STEP_STAGES];

export const getSupportMomentsStage = ({
  option,
  outcome,
  step,
  hasResource,
}: {
  option?: SupportOption;
  outcome: CheckInOutcome | null;
  step: number;
  hasResource: boolean;
}): SupportMomentsStage => {
  if (!option) return "generalError";
  if (outcome) return OUTCOME_STAGE[outcome];

  const stepStages = option === SupportOption.NotSure ? NOT_SURE_JOURNEY_STEP_STAGES : JOURNEY_STEP_STAGES;
  const stage = stepStages[step - 1];
  return stage === "recommendedResource" && !hasResource ? "resourceNotFound" : stage;
};

export const getFeelingForResource = (
  resourcesByFeeling: FeelingResources[] | undefined,
  feelings: SupportMomentFeelingType[],
  resourceId?: string
): SupportMomentFeelingType | undefined =>
  resourcesByFeeling?.find((f) => feelings.includes(f.feeling) && f.resources.some((r) => r.id === resourceId))?.feeling;
